/**
 * Tier Configuration Editor
 * Allows businesses to customize tier thresholds and multipliers
 *
 * Features:
 * - Visual tier progression
 * - Real-time validation
 * - Preview of customer journey
 * - Drag-to-adjust sliders
 * - Industry template presets
 */

import React, { useState } from 'react';
import { Card, Input } from './ui';

export interface TierConfig {
  bronze: { threshold: number; multiplier: number; name?: string };
  silver: { threshold: number; multiplier: number; name?: string };
  gold: { threshold: number; multiplier: number; name?: string };
  platinum: { threshold: number; multiplier: number; name?: string };
}

interface TierConfigurationEditorProps {
  initialConfig?: TierConfig;
  onChange?: (config: TierConfig) => void;
  showPresets?: boolean;
}

const DEFAULT_CONFIG: TierConfig = {
  bronze: { threshold: 0, multiplier: 1.0, name: 'Bronze' },
  silver: { threshold: 500, multiplier: 1.2, name: 'Silver' },
  gold: { threshold: 2000, multiplier: 1.5, name: 'Gold' },
  platinum: { threshold: 5000, multiplier: 2.0, name: 'Platinum' },
};

const TIER_PRESETS = {
  cafe: {
    bronze: { threshold: 0, multiplier: 1.0 },
    silver: { threshold: 300, multiplier: 1.25 },
    gold: { threshold: 1000, multiplier: 1.5 },
    platinum: { threshold: 3000, multiplier: 2.0 },
  },
  restaurant: {
    bronze: { threshold: 0, multiplier: 1.0 },
    silver: { threshold: 500, multiplier: 1.2 },
    gold: { threshold: 2000, multiplier: 1.5 },
    platinum: { threshold: 5000, multiplier: 2.0 },
  },
  retail: {
    bronze: { threshold: 0, multiplier: 1.0 },
    silver: { threshold: 1000, multiplier: 1.15 },
    gold: { threshold: 5000, multiplier: 1.3 },
    platinum: { threshold: 15000, multiplier: 1.5 },
  },
  salon: {
    bronze: { threshold: 0, multiplier: 1.0 },
    silver: { threshold: 400, multiplier: 1.3 },
    gold: { threshold: 1500, multiplier: 1.6 },
    platinum: { threshold: 4000, multiplier: 2.0 },
  },
  fitness: {
    bronze: { threshold: 0, multiplier: 1.0 },
    silver: { threshold: 600, multiplier: 1.25 },
    gold: { threshold: 2500, multiplier: 1.5 },
    platinum: { threshold: 7000, multiplier: 1.75 },
  },
};

const TIER_COLORS = {
  bronze: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', icon: '🥉' },
  silver: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-800', icon: '🥈' },
  gold: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', icon: '🥇' },
  platinum: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800', icon: '💎' },
};

export const TierConfigurationEditor: React.FC<TierConfigurationEditorProps> = ({
  initialConfig = DEFAULT_CONFIG,
  onChange,
  showPresets = true,
}) => {
  const [config, setConfig] = useState<TierConfig>(initialConfig);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateConfig = (newConfig: TierConfig): boolean => {
    const newErrors: Record<string, string> = {};

    // Check ascending thresholds
    if (newConfig.silver.threshold <= newConfig.bronze.threshold) {
      newErrors.silver = 'Silver threshold must be higher than Bronze';
    }
    if (newConfig.gold.threshold <= newConfig.silver.threshold) {
      newErrors.gold = 'Gold threshold must be higher than Silver';
    }
    if (newConfig.platinum.threshold <= newConfig.gold.threshold) {
      newErrors.platinum = 'Platinum threshold must be higher than Gold';
    }

    // Check ascending multipliers
    if (newConfig.silver.multiplier < newConfig.bronze.multiplier) {
      newErrors.silverMultiplier = 'Silver multiplier should be higher than Bronze';
    }
    if (newConfig.gold.multiplier < newConfig.silver.multiplier) {
      newErrors.goldMultiplier = 'Gold multiplier should be higher than Silver';
    }
    if (newConfig.platinum.multiplier < newConfig.gold.multiplier) {
      newErrors.platinumMultiplier = 'Platinum multiplier should be higher than Gold';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateTier = (
    tier: keyof TierConfig,
    field: 'threshold' | 'multiplier' | 'name',
    value: number | string
  ) => {
    const newConfig = {
      ...config,
      [tier]: {
        ...config[tier],
        [field]: field === 'name' ? value : parseFloat(value as string) || 0,
      },
    };

    setConfig(newConfig);
    validateConfig(newConfig);

    if (onChange) {
      onChange(newConfig);
    }
  };

  const applyPreset = (preset: keyof typeof TIER_PRESETS) => {
    const presetData = TIER_PRESETS[preset];
    const newConfig = {
      bronze: { ...config.bronze, ...presetData.bronze },
      silver: { ...config.silver, ...presetData.silver },
      gold: { ...config.gold, ...presetData.gold },
      platinum: { ...config.platinum, ...presetData.platinum },
    };

    setConfig(newConfig);
    validateConfig(newConfig);

    if (onChange) {
      onChange(newConfig);
    }
  };

  const tiers = ['bronze', 'silver', 'gold', 'platinum'] as const;

  return (
    <div className="space-y-6">
      {/* Presets */}
      {showPresets && (
        <div>
          <label className="block text-sm font-semibold text-black mb-2">Quick Presets</label>
          <div className="flex flex-wrap gap-2">
            {Object.keys(TIER_PRESETS).map((preset) => (
              <button
                key={preset}
                onClick={() => applyPreset(preset as keyof typeof TIER_PRESETS)}
                className="px-3 py-1.5 text-sm bg-white border border-border-light rounded-md hover:border-orange-400 hover:bg-orange-50 transition-colors"
              >
                {preset.charAt(0).toUpperCase() + preset.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tier Editor */}
      <div className="space-y-4">
        {tiers.map((tier) => {
          const tierData = config[tier];
          const colors = TIER_COLORS[tier];
          const isFirst = tier === 'bronze';

          return (
            <Card
              key={tier}
              className={`border-2 ${colors.border} ${colors.bg} transition-all duration-200`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{colors.icon}</span>
                    <div>
                      <input
                        type="text"
                        value={tierData.name || tier}
                        onChange={(e) => handleUpdateTier(tier, 'name', e.target.value)}
                        className={`text-base font-bold ${colors.text} bg-transparent border-none outline-none focus:ring-2 focus:ring-orange-400 rounded px-1`}
                        placeholder={`${tier.charAt(0).toUpperCase() + tier.slice(1)} Tier`}
                      />
                      <p className="text-xs text-black-lighter mt-0.5">
                        {isFirst ? 'Starting tier for all customers' : `Unlocks at ${tierData.threshold.toLocaleString()} Stars`}
                      </p>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full ${colors.bg} border ${colors.border}`}>
                    <span className={`text-sm font-semibold ${colors.text}`}>
                      {tierData.multiplier}x rewards
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Threshold */}
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      Threshold (Stars)
                    </label>
                    <Input
                      type="number"
                      value={tierData.threshold}
                      onChange={(e) => handleUpdateTier(tier, 'threshold', e.target.value)}
                      disabled={isFirst}
                      min={0}
                      step={100}
                      className={`${isFirst ? 'bg-cream-100 cursor-not-allowed' : ''}`}
                    />
                    {errors[tier] && (
                      <p className="text-xs text-red-600 mt-1">{errors[tier]}</p>
                    )}
                  </div>

                  {/* Multiplier */}
                  <div>
                    <label className="block text-xs font-semibold text-black mb-1">
                      Multiplier
                    </label>
                    <Input
                      type="number"
                      value={tierData.multiplier}
                      onChange={(e) => handleUpdateTier(tier, 'multiplier', e.target.value)}
                      min={1}
                      max={5}
                      step={0.1}
                    />
                    {errors[`${tier}Multiplier`] && (
                      <p className="text-xs text-red-600 mt-1">{errors[`${tier}Multiplier`]}</p>
                    )}
                  </div>
                </div>

                {/* Visual Progress Bar */}
                <div className="mt-4">
                  <div className="relative h-2 bg-cream-200 rounded-full overflow-hidden">
                    <div
                      className={`absolute h-full ${colors.bg} opacity-60`}
                      style={{
                        width: tier === 'platinum' ? '100%' : '25%',
                        left: tier === 'bronze' ? '0%' : tier === 'silver' ? '25%' : tier === 'gold' ? '50%' : '75%',
                      }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Preview Section */}
      <Card className="border border-border-light">
        <div className="p-4">
          <h4 className="text-sm font-bold text-black mb-3">Customer Journey Preview</h4>
          <div className="space-y-2 text-xs">
            <div className="flex items-center justify-between py-2 border-b border-border-light">
              <span className="text-black-lighter">Customer spends $100</span>
              <span className="font-semibold text-black">Earns 1,000 Stars</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border-light">
              <span className="text-black-lighter">At {config.silver.threshold} Stars →</span>
              <span className="font-semibold text-gray-700">
                🥈 Silver ({config.silver.multiplier}x earnings)
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-border-light">
              <span className="text-black-lighter">At {config.gold.threshold} Stars →</span>
              <span className="font-semibold text-yellow-700">
                🥇 Gold ({config.gold.multiplier}x earnings)
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-black-lighter">At {config.platinum.threshold} Stars →</span>
              <span className="font-semibold text-purple-700">
                💎 Platinum ({config.platinum.multiplier}x earnings)
              </span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              💡 <strong>Example:</strong> A Platinum customer spending $50 would earn{' '}
              <strong>{(500 * config.platinum.multiplier).toLocaleString()} Stars</strong> instead of 500 Stars
            </p>
          </div>
        </div>
      </Card>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Configuration Issues</p>
          <ul className="text-xs text-red-800 space-y-1">
            {Object.values(errors).map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TierConfigurationEditor;
